import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';

import didUpdate from 'webapp/modifiers/did-update';
import nodeResize, { Orientation } from 'webapp/modifiers/node-resize';
import type { HtmlEditorOptions } from 'webapp/components/form/html-editor';
import FormHtmlEditorComponent from 'webapp/components/form/html-editor';

import type ReportSlide from 'webapp/models-custom/report-slide';

interface Args {
	model: ReportSlide;
	onUpdateSlide: (slide: ReportSlide) => void;
}

export default class ReportsEditorDescriptionComponent extends Component<Args> {
	@service('flow/tour/reports-slides')
	declare flowTourReportsSlidesService: Service.Flow.Tour.ReportsSlides;

	@tracked isEditorReady = true; // used to cleanly re-initialize the html editor
	@tracked currentModel: ReportSlide; // when switching between slides, we still might be updating the previous slide description

	nodeResizeDirection = Orientation.VERTICAL;

	placeholder = 'Click to edit note...';

	availableTourId = TourId.REPORTS__ADD_NOTE;

	options: HtmlEditorOptions = {
		placeholder: this.placeholder,
		toolbar: 'bold italic underline | link',
		plugins: ['link', 'autolink', 'paste', 'wordcount'],
		contextmenu: false,
		// paste plugin
		paste_block_drop: true,
		smart_paste: false,
		paste_as_text: true,
		// link plugin
		default_link_target: '_blank',
		link_assume_external_targets: 'https',
		link_default_protocol: 'https',
		link_title: false,
		target_list: false,
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.currentModel = this.args.model;

		schedule('afterRender', () => {
			this.setupTourBindings();
		});
	}

	setupTourBindings() {
		this.flowTourReportsSlidesService.bindTourCheck(this.availableTourId, this, () => {
			this.flowTourReportsSlidesService.startTour(this.availableTourId);
		});
	}

	get text() {
		return this.args.model.description.content;
	}

	@action
	onModelChange(): void {
		// an ACTUAL slide switch, not just saving current slide
		if (this.currentModel.id !== this.args.model.id) {
			this.isEditorReady = false;
			schedule('afterRender', () => {
				next(() => {
					this.currentModel = this.args.model;
					this.isEditorReady = true;
				});
			});
		}
	}

	@action
	onResize(): void {
		$(window).trigger('resize.datawallcenter');
	}

	@action
	onTextChange(text: string): void {
		if (text !== this.text && text !== this.placeholder) {
			const slideModel = this.currentModel;
			slideModel.setDescription(text);
			this.args.onUpdateSlide(slideModel);

			this.flowTourReportsSlidesService.endTour(this.availableTourId);
			this.flowTourReportsSlidesService.checkTour();
		}
	}

	override willDestroy(): void {
		super.willDestroy();

		this.flowTourReportsSlidesService.unbindTourCheck(this.availableTourId, this);
	}
	<template>
		<div
			class="report_slide_desc"
			{{didUpdate this.onModelChange @model}}
			{{nodeResize this.nodeResizeDirection this.onResize 0.5}}
		>
			<span class="resize-handle"></span>
			<div class="content">

				{{#if this.isEditorReady}}
					<FormHtmlEditorComponent
						@text={{this.text}}
						@onChange={{this.onTextChange}}
						@options={{this.options}}
						@maxChars={{10000}}
					/>
				{{/if}}

			</div>
		</div>
	</template>
}
